import * as React from 'react';
import { Badge, Box } from '@chakra-ui/react';
import { withConfirm, SectionCard, ItemAmount, Total, SubHeading } from 'shared-components';

import { formatCurrency } from '../../../utilities/formatting';
import { FundingSummary } from '../../../types';
import { dependantChildrenLabel } from '../../../utilities/sponsors';

interface IProps {
  fundingSummary: FundingSummary;
}

function formatAmount(amount: number) {
  return `£${formatCurrency(amount)}`;
}

function renderStatus(itemStatus: string) {
  return (
    <Box display="flex" pr={4} mb={3} justifyContent="end">
      <Badge size="md" colorScheme={itemStatus === 'Complete' ? 'green' : 'orange'}>
        {itemStatus}
      </Badge>
    </Box>
  );
}

function RemainingFundingTable(props: IProps) {
  const { courseFees, studentLivingCost, spouseLivingCost, childrenLivingCost, totalRemaining } = props.fundingSummary;

  const COMPLETE = 'Complete';
  const OUTSTANDING = 'Outstanding';

  function renderCourseFee() {
    if (courseFees.length === 0) return null;

    const status = courseFees.some(courseFee => courseFee.remainingAmount > 0) ? OUTSTANDING : COMPLETE;

    return (
      <>
        {renderStatus(status)}

        <SectionCard data-selector="remaining-course-fee">
          {courseFees
            ? courseFees.map((item, i) => (
                <React.Fragment key={i}>
                  <Total label={item.label} amount={formatAmount(item.remainingAmount)} />

                  {item.funding
                    ? item.funding.map((source, i) => (
                        <ItemAmount key={i} label={source.label} amount={formatAmount(source.amount)} hasMargin />
                      ))
                    : null}
                </React.Fragment>
              ))
            : null}
        </SectionCard>
      </>
    );
  }

  function renderStudentLivingCost() {
    if (studentLivingCost.amount === 0) return null;

    const status = studentLivingCost.remainingAmount > 0 ? OUTSTANDING : COMPLETE;

    return (
      <>
        {renderStatus(status)}
        <SectionCard data-selector="remaining-student-living-cost">
          <Total label={studentLivingCost.label} amount={formatAmount(studentLivingCost.remainingAmount)} />

          {studentLivingCost
            ? studentLivingCost.funding.map((source, i) => (
                <ItemAmount key={i} label={source.label} amount={formatAmount(source.amount)} hasMargin />
              ))
            : null}
        </SectionCard>
      </>
    );
  }

  function renderDependantLivingCost() {
    if (spouseLivingCost.amount === 0 && childrenLivingCost.amount === 0) return null;

    const status =
      spouseLivingCost.remainingAmount > 0 || childrenLivingCost.remainingAmount > 0 ? OUTSTANDING : COMPLETE;

    return (
      <>
        {renderStatus(status)}
        <SectionCard data-selector="remaining-dependant-living-cost">
          {spouseLivingCost.amount !== 0 && (
            <>
              <Total label="Partner" amount={formatAmount(spouseLivingCost.remainingAmount)}></Total>

              {spouseLivingCost.funding.map((source, i) => (
                <ItemAmount key={i} label={source.label} amount={formatAmount(source.amount)} hasMargin />
              ))}
            </>
          )}

          {childrenLivingCost.amount !== 0 && (
            <>
              <Total
                label={dependantChildrenLabel(childrenLivingCost.number)}
                amount={formatAmount(childrenLivingCost.remainingAmount)}
              />

              {childrenLivingCost.funding.map((source, i) => (
                <ItemAmount key={i} label={source.label} amount={formatAmount(source.amount)} hasMargin />
              ))}
            </>
          )}
        </SectionCard>
      </>
    );
  }

  function renderTotal() {
    return (
      <SectionCard data-selector="remaining-total">
        <Total label="Total Remaining" amount={formatAmount(totalRemaining)} />
      </SectionCard>
    );
  }

  return (
    <div data-selector="remaining-table">
      <SubHeading>How your funds are allocated:</SubHeading>

      {renderCourseFee()}
      {renderStudentLivingCost()}
      {renderDependantLivingCost()}
      {renderTotal()}
    </div>
  );
}

export default withConfirm(RemainingFundingTable);
